import React, { useState } from 'react';
import { Lightbulb, AlertCircle, Plus } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import FeedbackList from '../components/feedback/FeedbackList';
import FeedbackFilters from '../components/feedback/FeedbackFilters';
import { mockFeatureRequests, mockProducts, mockCategories } from '../data/mockFeedback';
import { FeedbackFilters as Filters } from '../types/feedback';

export default function FeatureRequestPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [filters, setFilters] = useState<Filters>({});
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    useCase: '',
    benefits: '',
    alternatives: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would submit to an API
    console.log('Feature request submitted:', formData);
    setShowForm(false);
    setFormData({
      title: '',
      description: '',
      useCase: '',
      benefits: '',
      alternatives: ''
    });
  };

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <Lightbulb className="h-8 w-8 text-yellow-500" />
            <h1 className={`text-3xl font-bold ${
              theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
            }`}>
              Feature Requests
            </h1>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            <span>Request Feature</span>
          </button>
        </div>

        {showForm ? (
          <div className={`
            p-6 rounded-lg mb-8
            ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
          `}>
            <div className={`
              p-4 rounded-lg mb-6 flex items-start space-x-3
              ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}
            `}>
              <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
              <p className={`text-sm ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Have an idea for a new feature? We'd love to hear it! Please provide detailed information
                about your suggestion and how it would benefit the community.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Feature Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className={`
                    w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                    ${theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                    }
                  `}
                  placeholder="Brief description of the feature"
                  required
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className={`
                    w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                    ${theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                    }
                  `}
                  rows={4}
                  placeholder="Detailed description of the feature"
                  required
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Use Case
                </label>
                <textarea
                  value={formData.useCase}
                  onChange={(e) => setFormData(prev => ({ ...prev, useCase: e.target.value }))}
                  className={`
                    w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                    ${theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                    }
                  `}
                  rows={3}
                  placeholder="How would you use this feature?"
                  required
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Benefits
                </label>
                <textarea
                  value={formData.benefits}
                  onChange={(e) => setFormData(prev => ({ ...prev, benefits: e.target.value }))}
                  className={`
                    w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                    ${theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                    }
                  `}
                  rows={3}
                  placeholder="How would this feature benefit users?"
                  required
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Current Alternatives
                </label>
                <textarea
                  value={formData.alternatives}
                  onChange={(e) => setFormData(prev => ({ ...prev, alternatives: e.target.value }))}
                  className={`
                    w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                    ${theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                    }
                  `}
                  rows={3}
                  placeholder="How do you currently work around not having this feature?"
                />
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className={`
                    px-4 py-2 rounded-lg transition-colors
                    ${theme === 'dark'
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }
                  `}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        ) : (
          <>
            <FeedbackFilters
              filters={filters}
              onFilterChange={setFilters}
              products={mockProducts}
              categories={mockCategories}
            />
            <FeedbackList items={mockFeatureRequests} type="feature" />
          </>
        )}
      </div>
    </div>
  );
}